import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPencil,
  faPlus,
  faTrash,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { API_URL } from "../../config";
import Swal from "sweetalert2";
import LoadingSpinner from "../../components/LoadingSpinner";
import { PermissionWrapper, hasPermission } from "../../helpers/permission.js";

const IncentiveRuleList = () => {
  const [incentiveRules, setIncentiveRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState(null);
  const token = localStorage.getItem("authToken");

  // 🔎 Search Filters
  const [minTargetFilter, setMinTargetFilter] = useState("");
  const [maxTargetFilter, setMaxTargetFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [valueFilter, setValueFilter] = useState("");

  // 📑 Pagination
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;

  const navigate = useNavigate();

  const fetchtempEmail = async () => {
    try {
      setLoading(true);
      setTableLoading(true);
      const response = await axios.get(
        `${API_URL}settings/incentiverule/list`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setIncentiveRules(response.data.data || []); // Adjust this path if the API structure differs
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  };

  useEffect(() => {
    fetchtempEmail();
  }, []);

  // 🔎 Apply filters
  const filteredRules = useMemo(() => {
    return incentiveRules.filter((rule) => {
      if (
        minTargetFilter &&
        !(rule.min_target?.toString() || "")
          .toLowerCase()
          .includes(minTargetFilter.toLowerCase())
      ) {
        return false;
      }
      if (
        maxTargetFilter &&
        !(rule.max_target?.toString() || "")
          .toLowerCase()
          .includes(maxTargetFilter.toLowerCase())
      ) {
        return false;
      }
      if (typeFilter) {
        const typeText = rule.incentive_type == 0 ? "percentage" : "fixed";
        if (!typeText.includes(typeFilter.toLowerCase())) {
          return false;
        }
      }
      if (
        valueFilter &&
        !(rule.value?.toString() || "")
          .toLowerCase()
          .includes(valueFilter.toLowerCase())
      ) {
        return false;
      }
      return true;
    });
  }, [
    incentiveRules,
    minTargetFilter,
    maxTargetFilter,
    typeFilter,
    valueFilter,
  ]);

  // 📑 Update pagination when filters change
  useEffect(() => {
    setTotal(filteredRules.length);
    setPageCount(Math.ceil(filteredRules.length / limit));
    setPage(1);
  }, [filteredRules, limit]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const paginatedRules = filteredRules.slice(start - 1, end);

  // Delete Confirmation
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this record!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonClass: "btn-danger",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.delete(
          `${API_URL}settings/incentiverule/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        // Remove the deleted item from state
        setIncentiveRules((prev) => prev.filter((rule) => rule.id !== id));

        // Show message from server
        Swal.fire("Deleted!", "Your record has been deleted.", "success");
      } catch (error) {
        Swal.fire(
          "Error",
          error.response?.data?.message ||
          "Something went wrong while deleting.",
          "error"
        );
      }
    }
  };

  useEffect(() => {
    const handleClearFilters = () => {
      setMinTargetFilter("");
      setMaxTargetFilter("");
      setTypeFilter("");
      setValueFilter("");
    };
    window.addEventListener('clearInactiveRuleFilterEvent', handleClearFilters);

    return () => {
      window.removeEventListener('clearInactiveRuleFilterEvent', handleClearFilters);
    };
  }, []);




  return (
    <div
      id="page-container"
      className="page-header-fixed page-sidebar-fixed fade in"
    >
      <div id="content" className="">
        {/* Alert Box Placeholder */}
        <div className="row pb-2">
          <div className="col-md-6">
            <h4 className="panel-title pull-left">Manage Incentive Rule</h4>
          </div>
          <div className="col-md-6" style={{ textAlign: "right" }}>
            <PermissionWrapper module="settings_incentiverule" action="add">
              <Link
                to={"/Settings/IncentiveRule/add"}
                className="btn btn-default btn-sm"
              >
                <FontAwesomeIcon icon={faPlus} /> Add Incentive Rule
              </Link>
            </PermissionWrapper>
          </div>
        </div>

        <div className="panel panel-default with-rounded-corner with-shadow">
          {/* 📑 Pagination Controls */}
          {incentiveRules.length > 0 && (
            <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
              <div className="panel-heading border-0">
                <h4 className="panel-title m-0">Incentive Rules</h4>
              </div>
              <div className="d-inline-block">
                <span style={{ fontSize: "0.8rem" }} className="me-2">
                  {start} to {end} of {total}
                </span>
                <button
                  className="btn btn-default btn-sm"
                  title="Previous"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                &nbsp;
                <button
                  className="btn btn-default btn-sm"
                  title="Next"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === pageCount}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            </div>
          )}

          {loading || incentiveRules.length > 0 ? (
            <div className="table-responsive">
              <table className="table m-b-0">
                <thead>
                  <tr>
                    <th style={{ textAlign: "center" }}>#</th>
                    <th>Min Target</th>
                    <th>Max Target</th>
                    <th>Incentive Type</th>
                    <th>Value</th>
                    <th>Action</th>
                  </tr>
                  {/* 🔎 Search Filters Row */}
                  <tr>
                    <th></th>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search min..."
                        value={minTargetFilter}
                        onChange={(e) => setMinTargetFilter(e.target.value)}
                      />
                    </th>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search max..."
                        value={maxTargetFilter}
                        onChange={(e) => setMaxTargetFilter(e.target.value)}
                      />
                    </th>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search type..."
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                      />
                    </th>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search value..."
                        value={valueFilter}
                        onChange={(e) => setValueFilter(e.target.value)}
                      />
                    </th>
                    <th>
                      <button
                        className="btn btn-default btn-sm"
                        onClick={() => {
                          setMinTargetFilter("");
                          setMaxTargetFilter("");
                          setTypeFilter("");
                          setValueFilter("");
                        }}
                      >
                        Clear
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tableLoading ? (
                    Array.from({ length: 6 }).map((_, index) => (
                      <tr key={index} className="skeleton-row">
                        <td><div className="skeleton-box" /></td>
                        <td><div className="skeleton-box" /></td>
                        <td><div className="skeleton-box" /></td>
                        <td><div className="skeleton-box" /></td>
                        <td><div className="skeleton-box" /></td>
                        <td>
                          <div className="skeleton-box" style={{ width: "120px" }} />
                        </td>
                      </tr>
                    ))
                  ) : paginatedRules.length > 0 ? (
                    paginatedRules.map((post, index) => (
                      <tr key={post.id}>
                        <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                          {(page - 1) * limit + index + 1}
                        </td>
                        <td>{post.min_target}</td>
                        <td>{post.max_target}</td>
                        <td>{post.incentive_type == 0 ? "Percentage" : "Fixed"}</td>
                        <td>{post.value}</td>
                        <td>
                          <div className="d-flex gap-2">
                            <PermissionWrapper module="settings_incentiverule" action="edit">
                              <button
                                onClick={() =>
                                  navigate(`/Settings/IncentiveRule/edit/${post.id}`)
                                }
                                className="btn btn-warning btn-xs"
                                title="Edit"
                              >
                                <FontAwesomeIcon icon={faPencil} />
                              </button>
                            </PermissionWrapper>
                            <PermissionWrapper module="settings_incentiverule" action="delete">
                              <button
                                onClick={() => handleDelete(post.id)}
                                className="btn btn-danger btn-xs"
                                title="Delete"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </PermissionWrapper>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" style={{ textAlign: "center" }}>
                        No Records Found
                      </td>
                    </tr>
                  )}
                </tbody>

              </table>
            </div>
          ) : (
            <div className="p-3">No records found.</div>
          )}
        </div>
      </div>

      <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
        <i className="ti-arrow-up"></i>
      </a>
    </div>
  );
};

export default IncentiveRuleList;
